import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountOriginEnum } from 'src/app/Enums/AccountOriginEnum';
import { Guid } from 'src/app/models/Guid.model';
import { PartnerRecommendation } from 'src/app/models/Partner/PartnerRecommendation';
import { SignUp } from 'src/app/models/User/SignUp.model';
import { AuthenticationService } from 'src/app/services/authentication/authentication.service';
import { ConfigurationService } from 'src/app/services/configuration/configuration.service';
import { InvalidFieldMessageService } from 'src/app/services/invalid-field-message/invalid-field-message.service';
import { PartnerService } from 'src/app/services/partner/partner.service';
import { RegisterService } from 'src/app/services/register/register.service';
import { SessionService } from 'src/app/services/session/session.service';
import { isAvailable } from 'src/validators/document/document';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { AppConfigService } from 'src/app/services/app-config/app-config.service';

@Component({
  selector: 'app-signUp',
  templateUrl: './signUp.page.html',
  styleUrls: ['./signUp.page.scss'],
  providers: []
})
export class SignUpPage implements OnInit {

  get message() {
    return this.messageService
  }
  get form() {
    return this.registerFormGroup.controls;
  }

  registerFormGroup!: FormGroup;
  public loading: boolean = false;
  private specialCharacter = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
  private uppercaseLetter = /[A-Z]/;

  constructor(
    private formBuilder: FormBuilder,
    private messageService: InvalidFieldMessageService,
    private registerService: RegisterService,
    private authenticationService: AuthenticationService,
    private router: Router,
    private configService: ConfigurationService,
    private route: ActivatedRoute,
    private partnerService: PartnerService,
    private sessionService: SessionService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    public appConfig: AppConfigService) { }

  ngOnInit(): void {
    this.authenticationService.verifyIfIsLogged();
    this.createFormBuilder();
    this.getParameterIndicationIfExists();
  }

  private createFormBuilder() {
    this.registerFormGroup = this.formBuilder.group({
      document: ['', {
        asyncValidators: [isAvailable(this.registerService)],
        //updateOn: 'blur'
      }],
      email: ['', [Validators.required, Validators.maxLength(100), Validators.minLength(5), Validators.email]],
      name: ['', [Validators.required, Validators.maxLength(255), Validators.minLength(5)]],
      password: ['', [Validators.required, Validators.maxLength(30), Validators.minLength(8), Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/)]],
      repeatedPassword: ['', [Validators.required, Validators.maxLength(30), Validators.minLength(8)]],
      phone: ['', Validators.required],
      dialCode: [''],
      recommendationGuid: [null],
      hasReferralCode: [false],
      referralCode: ['']
    }, { validator: this.checkPasswords });

    this.form['hasReferralCode'].valueChanges.subscribe((checked: boolean) => {
      if (checked) {
        this.form['referralCode'].setValidators([Validators.required, Validators.minLength(4), Validators.maxLength(30)]);
      } else {
        this.form['referralCode'].clearValidators();
        this.form['referralCode'].setValue('');
      }
      this.form['referralCode'].updateValueAndValidity();
      this.cdr.detectChanges();
    });
  }

  private getParameterIndicationIfExists() {
    this.route.queryParams
      .subscribe((params: { ind: Guid } | any) => {
        if (params && params.ind) {
          this.partnerService.getPartnerRecommendationByGuid(params.ind).subscribe(recomendation => {
            if (recomendation != null)
              this.setCompanyRecomended(recomendation);
          });
        }
      });
  }

  private setCompanyRecomended(company: PartnerRecommendation) {
    this.registerFormGroup.setValue({
      recommendationGuid: company.guid,
      document: company.companyDocument,
      name: company.companyName,
      email: company.companyEmail,
      phone: '+' + company.companyDialCode + company.companyPhone,
      dialCode: company.companyDialCode,
      password: "",
      repeatedPassword: "",
      hasReferralCode: false,
      referralCode: ""
    });
    this.registerFormGroup.controls['document'].disable({ onlySelf: true });
  }

  private checkPasswords: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    let pass = group.get('password')?.value;
    let confirmPass = group.get('repeatedPassword')?.value
    if (pass === confirmPass) {
      group.get('repeatedPassword')?.value ? group.get('repeatedPassword')?.setErrors(null) : group.get('required')
    } else {
      group.get('repeatedPassword')?.setErrors({ notSame: true })
    }
    return null
  }

  private createObject(): SignUp {
    return {
      email: this.form['email'].value,
      fullName: this.form['name'].value,
      isAdmnistrator: true,
      password: this.form['password'].value,
      phone: this.returnOnlyPhoneNumberWithoutDialCode(),
      dialCode: this.form['dialCode'].value,
      currency: this.configService.detectCurrencyByLocale(window.navigator.language),
      language: this.configService.detectLanguageByLocale(window.navigator.language),
      origin: AccountOriginEnum.WEB,
      recommendationGuid: this.form['recommendationGuid'].value,
      document: this.form['document'].value,
      partnerSlug: this.appConfig.isPartner ? this.appConfig.partnerKey : null
    }
  }

  private returnOnlyPhoneNumberWithoutDialCode(): string {
    const onlyDialCode: string = this.form['dialCode'].value;
    const phoneWithDialCode: string = this.form['phone'].value
    return phoneWithDialCode.slice(onlyDialCode.length + 1, phoneWithDialCode.length);
  }

  dialCodeChange(dialCode: string) {
    this.form['dialCode'].setValue(dialCode);
  }

  public onReferralCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = input.value.replace(/[^a-zA-Z0-9_-]/g, '').toUpperCase();
    this.form['referralCode'].setValue(sanitized, { emitEvent: false });
    input.value = sanitized;
  }

  public async signup() {
    this.loading = true;
    this.registerFormGroup.markAllAsTouched();
    try {
      if (await this.isEmailValid()) {
        if (this.registerFormGroup.valid) {
          const hasReferralCode = this.form['hasReferralCode'].value;
          const referralCode = this.form['referralCode'].value;

          let partnerSellerId: string | null = null;

          if (hasReferralCode && referralCode) {
            partnerSellerId = await this.validateReferralCode(referralCode);
            if (!partnerSellerId) {
              this.loading = false;
              return;
            }
          }

          this.form['email'].setErrors(null);
          await this.registerService.register(this.createObject());

          if (hasReferralCode && referralCode && partnerSellerId) {
            await this.linkReferralCode(referralCode, partnerSellerId);
          }

          const isLoginCorrect = await this.authenticationService.signIn({
            email: this.form['email'].value,
            password: this.form['password'].value
          });
          if (isLoginCorrect) {
            this.nextPageAfterCodeValidation();
          }
        }
      }
      this.loading = false;
    } catch (err) {
      this.loading = false;
    }
  }

  private validateReferralCode(code: string): Promise<string | null> {
    return new Promise((resolve) => {
      this.partnerService.validateReferralCodeOnly(code).subscribe({
        next: (data) => {
          resolve(data.partnerSellerId);
        },
        error: () => {
          this.snackBar.open(this.translateService.instant('code.invalidCode'), '', {
            duration: 1800,
            verticalPosition: 'top',
            panelClass: ['red-snackbar']
          });
          resolve(null);
        }
      });
    });
  }

  private linkReferralCode(code: string, partnerSellerId: string): Promise<void> {
    return new Promise((resolve) => {
      this.partnerService.linkPartnerCodeToUserByData(code, partnerSellerId, this.createObject()).subscribe({
        next: () => {
          this.snackBar.open(this.translateService.instant('code.sucess'), '', {
            duration: 1800,
            verticalPosition: 'top',
            panelClass: ['green-snackbar']
          });
          resolve();
        },
        error: () => resolve()
      });
    });
  }

  private async nextPageAfterCodeValidation() {
    if (this.sessionService.getDetails().wizardFinished) {
      this.router.navigateByUrl("/home", { replaceUrl: true });
    } else this.router.navigate(["/wizard"]);
  }

  public async isEmailValid(): Promise<boolean> {
    if (this.form['email'].value && !this.form['email'].errors) {
      const r = await this.registerService.emailExists(this.form['email'].value)
      if (r) {
        this.form['email'].setErrors({ emailExists: true })
      } else {
        this.form['email'].setErrors(null)
      }
      return !r
    }
    return false
  }

  public justOneSpecialCharacter(event: KeyboardEvent, nomeCampo: string) {
    if (event.code.includes("Space") || event.key == "¨" || event.key == "´") {
      event.preventDefault();
      return;
    }
    if (this.specialCharacter.test(this.registerFormGroup.controls[nomeCampo].value)) {
      if (this.specialCharacter.test(String.fromCharCode(event.charCode))) {
        event.preventDefault();
      }
    }

    if (this.uppercaseLetter.test(this.registerFormGroup.controls[nomeCampo].value)) {
      if (this.uppercaseLetter.test(String.fromCharCode(event.charCode))) {
        event.preventDefault();
      }
    }
  }
}